/**
 * UserStatsCards Component
 * Cartes de statistiques pour le dashboard utilisateurs
 */

import { Users, Shield, ShieldCheck, Lock } from 'lucide-react';
import type { UserAdminStatsDTO } from '../../application/dto/dtos';

export type UserStats = UserAdminStatsDTO;

interface UserStatsCardsProps {
  stats: UserAdminStatsDTO | null;
  loading?: boolean;
}

export function UserStatsCards({ stats, loading = false }: UserStatsCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-surface rounded-lg border border-border p-6 animate-pulse"
          >
            <div className="h-4 bg-surface-overlay rounded w-20 mb-2"></div>
            <div className="h-8 bg-surface-overlay rounded w-16"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const cards = [
    {
      title: 'Total utilisateurs',
      value: stats.total,
      icon: Users,
      iconBg: 'bg-accent-light',
      iconColor: 'text-accent',
      subtitle: `${stats.actifs} actifs, ${stats.inactifs} inactifs`,
    },
    {
      title: 'Administrateurs',
      value: (stats.parRole.SUPER_ADMIN || 0) + (stats.parRole.ADMIN_CARITAS || 0) + (stats.parRole.ADMIN_NATIONAL || 0) + (stats.parRole.ADMIN_DIOCESAIN || 0),
      icon: Shield,
      iconBg: 'bg-primary-light',
      iconColor: 'text-primary-text',
      subtitle: `${stats.parRole.GESTIONNAIRE || 0} gestionnaires, ${stats.parRole.OBSERVATEUR || 0} observateurs`,
    },
    {
      title: 'Avec 2FA',
      value: stats.avec2FA,
      icon: ShieldCheck,
      iconBg: 'bg-success-light',
      iconColor: 'text-success',
      subtitle: `${stats.total > 0 ? Math.round((stats.avec2FA / stats.total) * 100) : 0}% du total`,
    },
    {
      title: 'Comptes verrouillés',
      value: stats.comptesBloques,
      icon: Lock,
      iconBg: 'bg-danger-light',
      iconColor: 'text-danger',
      subtitle: stats.comptesBloques > 0 ? 'Nécessite attention' : 'Aucun verrouillage',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => {
        const Icon = card.icon;

        return (
          <div
            key={index}
            className="bg-surface rounded-lg border border-border p-6
                     hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${card.iconBg} ${card.iconColor}`}>
                <Icon className="w-6 h-6" />
              </div>
            </div>

            <div>
              <p className="text-3xl font-bold text-content mb-1">
                {(card.value ?? 0).toLocaleString()}
              </p>
              <p className="text-sm font-medium text-content-muted mb-1">
                {card.title}
              </p>
              <p className="text-xs text-content-subtle">
                {card.subtitle}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
